import { useMatch, useMatches } from '@tanstack/react-router';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { useAppConfiguration } from '@mews-commander/data-access';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { hasPlatformScopeWithAccessAllEnterprises } from '@mews-commander/legacy-entities/src/ducks/scopes';
import { getBreakpointRangeDefault, isMobileOrTablet, MultilineText, WithBreakpointRange } from '@mews-commander/ui';
import { useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { PlatformEnvironment } from '@mews/enums';
import { LocalizedText, useFeatureFlag } from '@mews/framework';
import { useResolveRoute } from '@mews/routing';
import { MewsLogo, Tooltip, withBreakpointRange } from '@optimus-web/core';
import { getHomeUrl } from '../../../router/selectors';
import { forceReload, logError } from '../../app/actions/common';
import { getGlobalAdminLogoTitle } from '../../app/reducers/server';
import { DASHBOARD_SHORTCUT, navigateToHome } from '../../shortcuts';
import { LOGO_TOOLTIP_DELAY, TOOLTIP_MAX_WIDTH, TOOLTIP_OFFSET } from '../constants';
import { LogoWrapper } from './styles';

const useCurrentRouteOwner = () => {
    const matches = useMatches();
    return matches.at(-1)?.staticData?.owner;
};

const LogoMenu = ({ breakpointRange }: WithBreakpointRange) => {
    const dispatch = useCommanderThunkDispatch();
    const resolveRoute = useResolveRoute();
    const homeUrl = useCommanderSelector((state) => getHomeUrl(state, { resolveRoute }));
    const globalAdminTooltip = useCommanderSelector(getGlobalAdminLogoTitle);
    const currentEnterpriseId = useCurrentEnterpriseId();
    const canAccessAllEnterprises = useCommanderSelector(hasPlatformScopeWithAccessAllEnterprises);
    const { environment } = useAppConfiguration();
    const assistantEnabled: boolean = useFeatureFlag(FeatureFlag.assistantEnabled);
    const isEnterpriseDashboard = useMatch({
        from: '/_authenticated/$currentEnterpriseId/Dashboard/Index/',
        shouldThrow: false,
    });
    const isMultipropertyDashboard = useMatch({
        from: '/_authenticated/$currentEnterpriseId/EnterpriseRelationshipReport/Index/',
        shouldThrow: false,
    });

    const handleLogoClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (isEnterpriseDashboard || isMultipropertyDashboard) {
            dispatch(logError({ type: 'force-reload', message: 'Force reload caused by user reloading dashboard.' }));
            dispatch(forceReload());
        } else {
            dispatch(navigateToHome());
        }
    };

    const routeOwner = useCurrentRouteOwner();

    const titleElement = currentEnterpriseId ? (
        <span>
            {routeOwner && (canAccessAllEnterprises || environment === PlatformEnvironment.Development) ? (
                <>
                    {routeOwner}
                    <br />
                </>
            ) : null}
            <LocalizedText textKey={TextKey.Dashboard} />
            <br />
            {DASHBOARD_SHORTCUT}
        </span>
    ) : (
        <MultilineText text={`${routeOwner ? `${routeOwner}\n` : ''}${globalAdminTooltip}`} />
    );
    const iconLogo = (
        <MewsLogo
            type={
                isMobileOrTablet(assistantEnabled ? getBreakpointRangeDefault() : breakpointRange)
                    ? 'logomark'
                    : 'wordmark'
            }
            data-test-id="mews_logo"
        />
    );

    const wrapperProps = {
        href: homeUrl,
        tooltip: titleElement,
        tooltipDelay: LOGO_TOOLTIP_DELAY,
        onClick: handleLogoClick,
    };

    return (
        <TooltippedLogoWrapper maxWidth={TOOLTIP_MAX_WIDTH} offset={TOOLTIP_OFFSET} {...wrapperProps}>
            {iconLogo}
        </TooltippedLogoWrapper>
    );
};

const TooltippedLogoWrapper = Tooltip(LogoWrapper);

export default withBreakpointRange(LogoMenu);
